import ExcelJS from "exceljs/dist/exceljs.min.js";
import { nombreDiaFestivo } from "./diasFestivos";

const COLORES_TIPO = {
    Asistencia: "2E9E5B",
    Retardo: "F0A030",
    Falta: "D64545",
    Justificado: "4A90D9",
};

/**
 * Primera y última checada (HH:MM) de cada empleado por día.
 * resultado: { [nombre]: { [YYYY-MM-DD]: { primera, ultima, total } } }
 */
export const calcularChecadasPorDia = (empleados, checadas) => {
    const resultado = {};
    empleados.forEach((emp) => {
        const nombre = `${emp.datosPersonales?.nombre || ""} ${emp.datosPersonales?.apellidoPaterno || ""}`.trim();
        const numero = String(emp.numeroEmpleado || "").trim();
        const porDia = {};
        checadas.forEach((c) => {
            const dt = c?.parsed?.event_datetime;
            if (!dt || String(c?.parsed?.employee_number || "").trim() !== numero) return;
            (porDia[dt.slice(0, 10)] = porDia[dt.slice(0, 10)] || []).push(dt.slice(11, 16));
        });
        resultado[nombre] = {};
        Object.entries(porDia).forEach(([dia, horas]) => {
            const orden = [...horas].sort();
            resultado[nombre][dia] = { primera: orden[0], ultima: orden[orden.length - 1], total: orden.length };
        });
    });
    return resultado;
};

export const generarReporteMandosMedios = async ({ filas, checadasPorDia = {}, periodoKey }) => {
    const wb = new ExcelJS.Workbook();
    wb.creator = "Empleados QS";

    // ---------- Hoja 1: detalle con colores ----------
    const wsDetalle = wb.addWorksheet(`Mandos medios ${periodoKey}`);
    wsDetalle.columns = [
        { header: "Empleado", key: "Empleado", width: 30 },
        { header: "Fecha", key: "Fecha", width: 14 },
        { header: "Tipo", key: "Tipo", width: 14 },
        { header: "Descripción", key: "Descripción", width: 60 },
    ];
    wsDetalle.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2F3E52" } };
        cell.alignment = { horizontal: "center" };
    });
    filas.forEach((fila) => {
        const row = wsDetalle.addRow(fila);
        const color = COLORES_TIPO[fila.Tipo];
        if (color) {
            const cell = row.getCell("Tipo");
            cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: `FF${color}` } };
            cell.alignment = { horizontal: "center" };
        }
    });
    wsDetalle.views = [{ state: "frozen", ySplit: 1 }];

    // ---------- Datos para las gráficas ----------
    const nombres = Object.keys(checadasPorDia);

    // ---------- Hoja 2: calendario de checadas ----------
    const wsCalendario = wb.addWorksheet("Gráficas");
    wsCalendario.getCell("A1").value = `Reporte de mandos medios - ${periodoKey}`;
    wsCalendario.getCell("A1").font = { bold: true, size: 16, color: { argb: "FF2F3E52" } };

    // ---- Calendario del mes con las checadas de cada mando medio ----
    const [anioCal, mesCal] = periodoKey.split("-").map(Number);
    const diasEnMes = new Date(anioCal, mesCal, 0).getDate();
    const offsetInicio = (new Date(anioCal, mesCal - 1, 1).getDay() + 6) % 7; // lunes = 0
    const nombreMes = new Date(anioCal, mesCal - 1, 1).toLocaleString("es-MX", { month: "long", year: "numeric" });
    const tipoPorEmpleadoDia = {};
    filas.forEach((f) => { tipoPorEmpleadoDia[`${f.Empleado}|${f.Fecha}`] = f.Tipo; });

    wsCalendario.getCell("A3").value = `Calendario de checadas - ${nombreMes}`;
    wsCalendario.getCell("A3").font = { bold: true, size: 14, color: { argb: "FF2F3E52" } };

    let filaCal = 4;
    ["Lun", "Mar", "Mié", "Jue", "Vie"].forEach((d, i) => {
        const cell = wsCalendario.getRow(filaCal).getCell(i + 1);
        cell.value = d;
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2F3E52" } };
        cell.alignment = { horizontal: "center" };
    });
    for (let i = 1; i <= 7; i += 1) wsCalendario.getColumn(i).width = 30;

    filaCal += 1;
    const totalCeldas = Math.ceil((offsetInicio + diasEnMes) / 7) * 7;
    for (let idx = 0; idx < totalCeldas; idx += 1) {
        const dia = idx - offsetInicio + 1;
        if (idx % 7 >= 5) continue; // solo lunes a viernes
        const col = (idx % 7) + 1;
        const row = wsCalendario.getRow(filaCal + Math.floor(idx / 7));
        const cell = row.getCell(col);
        cell.border = {
            top: { style: "thin", color: { argb: "FFCCCCCC" } },
            left: { style: "thin", color: { argb: "FFCCCCCC" } },
            bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
            right: { style: "thin", color: { argb: "FFCCCCCC" } },
        };
        cell.alignment = { vertical: "top", wrapText: true };
        if (dia < 1 || dia > diasEnMes) {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } };
            continue;
        }
        const fecha = `${periodoKey}-${String(dia).padStart(2, "0")}`;
        const festivo = nombreDiaFestivo(fecha);
        if (festivo) {
            cell.value = {
                richText: [
                    { text: String(dia), font: { bold: true, size: 12, color: { argb: "FF7A8B99" } } },
                    { text: `\nDía festivo: ${festivo}`, font: { size: 10, italic: true, color: { argb: "FF7A8B99" } } },
                ],
            };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE8ECF0" } };
            row.height = Math.max(row.height || 0, 48);
            continue;
        }
        const runs = [{ text: String(dia), font: { bold: true, size: 12, color: { argb: "FF2F3E52" } } }];
        let lineas = 1;
        nombres.forEach((n) => {
            const ch = (checadasPorDia[n] || {})[fecha];
            const tipo = tipoPorEmpleadoDia[`${n}|${fecha}`];
            if (!ch && !tipo) return;
            const color = COLORES_TIPO[tipo] || (ch ? COLORES_TIPO.Asistencia : "7A8B99");
            const detalle = ch
                ? (ch.total > 1 ? `${ch.primera} - ${ch.ultima}` : ch.primera)
                : tipo;
            runs.push({ text: `\n${n.split(" ")[0]}: ${detalle}`, font: { size: 10, bold: true, color: { argb: `FF${color}` } } });
            lineas += 1;
        });
        cell.value = { richText: runs };
        row.height = Math.max(48, 16 + lineas * 14);
    }
    const filasSemanas = totalCeldas / 7;
    const filaLeyenda = filaCal + filasSemanas + 1;
    Object.entries(COLORES_TIPO).forEach(([tipo, color], i) => {
        const cell = wsCalendario.getRow(filaLeyenda).getCell(i + 1);
        cell.value = tipo;
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: `FF${color}` } };
        cell.alignment = { horizontal: "center" };
    });

    // ---------- Descarga ----------
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reporte_mandos_medios_${periodoKey}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};
